import { Button } from '@/components/button';
import styles from './patient-details-confirmation.module.css';
import { useTranslation } from '@/contexts/i18n-context';
import { Section } from '@/components/containers/section';
import { useLoggedUser } from '@/contexts/current-user-context';
import { PageContainer } from '@/components/containers/page-container';
import { FormButtonRow } from '@/components/containers/form-button-row';
import { useBranding } from '@/outlets/branding-outlet';
import { formatDate } from '@/util/formats';
import { Navigate, useNavigate } from '@/util/react-router-dom';
import { SectionHeader } from '@/components/section-header';
import { useGetPatient } from '@/api/get-personal-info';
import { useUpdatePatientExternalInfo } from '@/api/update-external-info';
import { Loader } from '@/components/loaders/loader';
import { useMediaQuery } from '@/hooks/use-media-query';
import { NavButton } from '@/components/navigation/nav-button';
import { Banner } from '@/components/banner';
import clsx from 'clsx';
import { useLockPageNavigation } from '@/hooks/use-lock-page-navigation';
import { useIsVisitInProgress } from '@/hooks/use-is-visit-in-progress';

export function PatientDetailsConfirmation() {
  const { t } = useTranslation();
  const { logout, user } = useLoggedUser();
  const { data: patient, isFetching: isFetchingPatient } = useGetPatient(user.id);
  const { isVisitInProgress, isFetching: isFetchingVisitInProgress } = useIsVisitInProgress();
  const { logoutUrl, logoutText, refetch, name, humanizationTheme } = useBranding();
  const navigate = useNavigate();
  useLockPageNavigation();
  const {
    mutate: updateExternalInfo,
    isPending: isUpdatingExternalInfo,
    isError,
  } = useUpdatePatientExternalInfo({
    mutation: {
      onSuccess: () => navigate('/'),
    },
  });
  const handleLogout = () => {
    logout();
    refetch();
  };

  const returnLink = logoutUrl && logoutText ? { url: logoutUrl, text: logoutText } : null;

  if (isFetchingPatient || isFetchingVisitInProgress) {
    return <Loader type="large" />;
  }

  if (!patient) {
    return <Navigate to="/system-error" />;
  }

  const pendingExternalInfo = patient.pendingExternalInfo;
  const pendingPatientIdentity = pendingExternalInfo?.patient;
  const pendingSurrogateIdentity = pendingExternalInfo?.surrogate;

  return (
    <PageContainer>
      <div data-allow-leave>
        {name && humanizationTheme && (
          <PatientDetailsConfirmationBanner
            brandingName={name}
            humanizationTheme={humanizationTheme}
            returnLink={returnLink}
          />
        )}
      </div>
      <Section className={styles.detailsContainer}>
        {isError && (
          <Banner type="error" focusOnMount>
            {t('common.errors.unknown')}
          </Banner>
        )}
        {pendingExternalInfo && (
          <Banner type="warning" label={t('patientDetailsConfirmation.identityChangeWarning')} />
        )}
        {isVisitInProgress && (
          <Banner type="warning" label={t('patientDetailsConfirmation.visitInProgressWarning')} />
        )}
        <h1 className="h4">{t('patientDetailsConfirmation.title')}</h1>
        <div>
          {patient.hasExternalSurrogate && patient.surrogate && (
            <SectionHeader
              className={styles.sectionHeader}
              title={t('patientDetailsConfirmation.sections.patient')}
            />
          )}
          <dl className={styles.sectionContent}>
            <dt>{t('patientDetailsConfirmation.labels.name')}</dt>
            <dd className="bold">
              {pendingPatientIdentity?.firstName ?? patient.firstName}{' '}
              {pendingPatientIdentity?.lastName ?? patient.lastName}
            </dd>
            <dt>{t('patientDetailsConfirmation.labels.birthday')}</dt>
            <dd className="bold">
              {formatDate(pendingPatientIdentity?.birthday ?? patient.dateOfBirth)}
            </dd>
          </dl>
        </div>
        {patient.hasExternalSurrogate && patient.surrogate && (
          <div>
            <SectionHeader
              className={styles.sectionHeader}
              title={t('patientDetailsConfirmation.sections.surrogate')}
            />
            <dl className={styles.sectionContent}>
              <dt>{t('patientDetailsConfirmation.labels.name')}</dt>
              <dd className="bold">
                {pendingSurrogateIdentity?.firstName ?? patient.surrogate.firstName}{' '}
                {pendingSurrogateIdentity?.lastName ?? patient.surrogate.lastName}
              </dd>
              <dt>{t('patientDetailsConfirmation.labels.birthday')}</dt>
              <dd className="bold">
                {formatDate(pendingSurrogateIdentity?.birthday ?? patient.surrogate.birthday)}
              </dd>
            </dl>
          </div>
        )}
      </Section>

      <div data-allow-leave>
        <FormButtonRow
          buttonTextKey="patientDetailsConfirmation.confirmButton"
          description={t('patientDetailsConfirmation.subTitle')}
          type="button"
          onClick={() => updateExternalInfo({ patientId: user.id })}
          loading={isUpdatingExternalInfo}
          variant="filled"
          secondaryButton={
            <Button
              variant="outlined"
              color="accent"
              {...(returnLink
                ? { as: 'a', to: returnLink.url }
                : { as: 'button', onClick: handleLogout })}
            >
              {t('patientDetailsConfirmation.cancelButton')}
            </Button>
          }
        />
      </div>
    </PageContainer>
  );
}

interface PatientDetailsConfirmationBannerProps {
  brandingName: string;
  humanizationTheme: string;
  returnLink: { url: string; text: string } | null | undefined;
}

function PatientDetailsConfirmationBanner({
  brandingName,
  humanizationTheme,
  returnLink,
}: PatientDetailsConfirmationBannerProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('--screen-md');

  const bannerStyle = {
    '--bg-desktop-image': `url('/humanization/${humanizationTheme}/background-desktop.png')`,
    '--bg-mobile-image': `url('/humanization/${humanizationTheme}/background-mobile.png')`,
  } as React.CSSProperties;

  const backButton = returnLink ? (
    <NavButton to={returnLink.url}>{returnLink.text}</NavButton>
  ) : null;

  return (
    <div className={styles.bannerWrapper} style={bannerStyle}>
      {!isMobile && backButton}
      <div className={styles.bannerCard}>
        {isMobile && backButton}
        <div className={styles.bannerRow}>
          <div className={styles.bannerContent}>
            <h2 className={clsx(isMobile ? 'h4' : 'h3', styles.title)}>
              {t('patientDetailsConfirmation.bannerDescription', { brandingName })}
            </h2>
          </div>
          <div className={styles.bannerImageContainer}>
            <img
              src="/doc-team-member-log-sign-in.png"
              alt={t('patientDetailsConfirmation.dermatologistAlt')}
              className={styles.bannerImage}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
